import { useEffect, useState } from "react"
import { ParentPageInfo, hasParentIFrame, parentSubscribePageInfo } from "./parentIFrame"

/** Abstand des Overlays zur sichtbaren Oberkante des iFrame-Ausschnitts. */
export const OVERLAY_MARGIN = 24

/**
 * Berechnet die top-Position (px, relativ zum iFrame-Dokument) für ein
 * Modal/Overlay, sodass es im aktuell sichtbaren Ausschnitt des auto-Höhe-
 * iFrames erscheint. Hintergrund: Das iFrame ist so hoch wie sein Inhalt —
 * position:fixed-Overlays mit AntD-Default (top:100px bzw. centered) landen
 * daher außerhalb des sichtbaren Bereichs, sobald der Nutzer in der
 * Kundenseite gescrollt hat.
 *
 * Sichtbarer Ausschnitt: [scrollTop - offsetTop, + clientHeight] des Parents,
 * begrenzt auf die iFrame-Höhe.
 */
export function computeOverlayTop(info: ParentPageInfo, margin: number = OVERLAY_MARGIN): number {
    const iframeHeight = Math.max(0, info.iframeHeight)
    const rawTop = info.scrollTop - info.offsetTop
    // ponytail: Modal-Höhe ist unbekannt — Overlay beginnt knapp unter der
    // sichtbaren Oberkante statt zentriert. Reicht, solange Auslöser sichtbar
    // sind; echte Zentrierung bräuchte eine Messung der Modal-Höhe.
    const visibleTop = Math.min(Math.max(0, rawTop), iframeHeight)
    return visibleTop + margin
}

/**
 * Liefert die iframe-sichere top-Position für ein Modal, sobald es geöffnet
 * wird. Außerhalb eines Embeds (kein parentIFrame, z. B. Standalone oder alte
 * Embeds) kommt undefined zurück — dann gilt das AntD-Default-Verhalten, das
 * dort korrekt ist. Gemessen wird einmalig beim Öffnen (kein Live-Nachführen,
 * das Modal soll beim Parent-Scroll nicht springen).
 */
export function useIframeOverlayTop(open: boolean): number | undefined {
    const [top, setTop] = useState<number | undefined>(undefined)

    useEffect(() => {
        if (!open || !hasParentIFrame()) return undefined
        let received = false
        // Callback kommt garantiert asynchron (postMessage-Roundtrip zum
        // Parent) — `unsubscribe` ist beim ersten Aufruf also immer zugewiesen.
        const unsubscribe = parentSubscribePageInfo((info) => {
            if (received) return
            received = true
            setTop(computeOverlayTop(info))
            unsubscribe()
        })
        return unsubscribe
    }, [open])

    return top
}
